import React from 'react';
import { Box, Card, Typography, Skeleton, Tooltip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import { useThemeColors } from '../theme/ThemeModeContext';

/**
 * Aurora-style KPI tile (post-template-pass).
 *
 * Layout (top → bottom):
 *   1. Small uppercase label (overline)
 *   2. Big value, **light weight** (300/400) with units inline
 *   3. Optional small caption row beneath
 *   4. Optional delta as a *tinted pill* (green for up, red for down) at the
 *      bottom — mirrors the Aurora "+4.33% vs last month" pattern.
 *
 * The light font-weight on the value is the single most distinctive Aurora
 * upgrade — keep it under 400 unless the design specifically calls for emphasis.
 *
 * Props:
 *   label    : string         — uppercase tile label
 *   value    : string|number  — formatted main number
 *   unit     : string         — small unit text rendered after the value
 *   delta    : { value, label, direction: 'up'|'down'|'flat' } — optional trend
 *   loading  : bool
 *   tooltip  : string         — optional, shown on hover of the label
 *   accent   : color string   — optional left-edge accent stripe
 *   placeholder : bool        — render a "Pending" skeleton with the label only
 */
export default function KpiCard({
  label, value, unit, delta, loading, tooltip, accent, placeholder,
}) {
  const colors = useThemeColors();
  const dirIcon =
    delta?.direction === 'up'   ? <TrendingUpIcon   sx={{ fontSize: 13 }} /> :
    delta?.direction === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> :
                                  <RemoveIcon       sx={{ fontSize: 13 }} />;

  // Aurora pill: tinted 12% bg + solid color text, small + rounded
  const pillTint =
    delta?.direction === 'up'   ? '#22c55e' :
    delta?.direction === 'down' ? '#ef4444' :
                                  colors.textDim;

  return (
    <Card
      sx={{
        position: 'relative',
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        overflow: 'hidden',
        transition: 'border-color 120ms ease',
        '&:hover': { borderColor: colors.hairline2 },
      }}
    >
      {accent && (
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
          background: accent, borderTopLeftRadius: 12, borderBottomLeftRadius: 12,
        }} />
      )}

      <Tooltip title={tooltip || ''} arrow placement="top-start" disableHoverListener={!tooltip}>
        <Typography variant="overline" sx={{
          color: 'text.secondary',
          cursor: tooltip ? 'help' : 'default',
          fontSize: '0.68rem',
          lineHeight: 1.35,
          display: 'block',
        }}>
          {label}
        </Typography>
      </Tooltip>

      {/* mt:auto pins the value to the bottom of the card. Tiles in a grid row are
          stretched to equal height, so without it a label that wraps to two lines
          pushes its own value down and it no longer lines up with its neighbours. */}
      {loading ? (
        <Skeleton variant="text" width="60%" height={44}
                  sx={{ bgcolor: colors.skeleton, mt: 'auto' }} />
      ) : placeholder ? (
        <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem', fontStyle: 'italic', mt: 'auto' }}>
          Pending
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap',
                   mt: 'auto' }}>
          <Typography component="div" sx={{
            // Aurora light-weight display style
            fontSize: '1.95rem',
            fontWeight: 400,
            lineHeight: 1.05,
            letterSpacing: '-0.025em',
            color: 'text.primary',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {value}
          </Typography>
          {unit && (
            <Typography sx={{
              fontSize: '0.82rem', color: 'text.secondary', fontWeight: 500,
            }}>
              {unit}
            </Typography>
          )}
        </Box>
      )}

      {!loading && !placeholder && delta && (
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.4,
            px: 0.85, py: 0.25, borderRadius: '6px',
            bgcolor: `${pillTint}1f`,   // 12% alpha
            color: pillTint,
            fontSize: '0.72rem', fontWeight: 700,
            lineHeight: 1, letterSpacing: '0.01em',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {dirIcon}
            <Box component="span">{delta.value}</Box>
          </Box>
          {delta.label && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.74rem' }}>
              {delta.label}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
}

// ── Number formatters used across the dashboard ─────────────────────────────

export const fmtAUDCompact = (n) => {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `A$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `A$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `A$${(n / 1e3).toFixed(1)}k`;
  return `A$${Math.round(n).toLocaleString()}`;
};

export const fmtMWhCompact = (n) => {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)} TWh`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)} GWh`;
  return `${Math.round(n).toLocaleString()} MWh`;
};

export const fmtPct = (n, digits = 1) =>
  n == null || isNaN(n) ? '—' : `${n.toFixed(digits)}%`;

export const fmtAUDPerMWh = (n) =>
  n == null || isNaN(n) ? '—' : `A$${n.toFixed(2)}`;
